import { Component, ViewEncapsulation, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { debounceTime } from "rxjs/operators";

import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { TidyStorage } from 'src/shared/providers/tidy-storage';

import { Menu } from 'src/providers/menu/menu';
import { Workflows } from 'src/providers/workflows/workflows';

import { DateTime as LuxonDateTime } from 'luxon';

import { WorkflowRunPage } from 'src/pages/workflows/workflow-run/workflow-run';
import { Util } from 'src/shared/util/util';
import { trigger, state, style, transition, animate } from '@angular/animations';

@Component({
    templateUrl: 'workflow-history.html',
    encapsulation: ViewEncapsulation.None,
    styleUrls: ["./workflow-history.scss"],
    animations: [
        trigger('slideAnimation', [
            state('collapsed', style({
                height: '0px',
                overflow: 'hidden',
                opacity: '0'
            })),
            state('expanded', style({
                height: '*',
                overflow: 'visible',
                opacity: '1'
            })),
            transition('collapsed <=> expanded', [
                animate('300ms ease-in-out')
            ])
        ])
    ],
    standalone: false
})

export class WorkflowHistoryPage implements OnInit {

  allTriggers: any;
  allActions: any;
  allHistoryLength: any;
  dateRangeForm: UntypedFormGroup;
  errorMessage: string;
  history: any;
  loaded: boolean;
  isRightSideContent: boolean;
  pageNumber: any;
  pageSize: any;
  workflowItems: any;
  workflowsLoaded: boolean;
  searchTerm: string;
  searchForm: UntypedFormGroup;
  selectOptions: any;
  selectedWorkflowIds: any;
  startDate: any;
  endDate: any;
  workflows: any;
  selectedWorkflowValue = 0;
  expandedSourceData: { [key: string]: boolean } = {};

  private readonly STORAGE_KEYS = {
    SELECTED_WORKFLOW: 'workflow-history-selected-workflow',
    DATE_RANGE: 'workflow-history-date-range',
    SEARCH_TERM: 'workflow-history-search-term',
    PAGE_NUMBER: 'workflow-history-page-number',
    PAGE_SIZE: 'workflow-history-page-size'
  };

  constructor(
    fb: UntypedFormBuilder,
    private menu: Menu,
    private navCtrl: CustomNavController,
    private rightSidePanelService: RightSidePanelService,
    private storage: TidyStorage,
    private workflowsProvider: Workflows,
    public utils: Util,
  ) {
    this.dateRangeForm = fb.group({
      start: '',
      end: '',
      customStringSelect: ''
    });
    this.searchForm = fb.group({
      search: [''],
    });
    this.searchForm.valueChanges
      .pipe(debounceTime(300))
      .subscribe((value) => this.updateSearch(value));
  }

  async ngOnInit() {
    try {
      this.loaded = false;
      this.rightSidePanelService.setDialogPageTitle('Workflow Run History');
      this.isRightSideContent = await this.storage.retrieve('dialog-right-side-open') || false;

      // Restore saved state or use defaults
      this.pageNumber = await this.storage.retrieve(this.STORAGE_KEYS.PAGE_NUMBER) || 1;
      this.pageSize = await this.storage.retrieve(this.STORAGE_KEYS.PAGE_SIZE) || 10;
      this.searchTerm = await this.storage.retrieve(this.STORAGE_KEYS.SEARCH_TERM) || null;

      this.selectOptions = [
        { viewValue: 'Custom', value: 'custom' },
        { viewValue: 'Today', value: 'today' },
        { viewValue: 'Last Week', value: 'last_week' },
        { viewValue: 'Last Month', value: 'last_month' },
        { viewValue: 'All Time', value: 'all_time' }
      ];

      this.menu.selectedAutomation = 'workflow-history';
      this.allTriggers = await this.workflowsProvider.getWorkflowTriggerTypes();
      this.allActions = await this.workflowsProvider.getWorkflowActionTypes();
      await this.buildWorkflowFilter();

      // Restore date range or use default
      const savedDateRange = await this.storage.retrieve(this.STORAGE_KEYS.DATE_RANGE);
      if (savedDateRange) {
        this.startDate = savedDateRange.start;
        this.endDate = savedDateRange.end;
        this.dateRangeForm.patchValue({
          start: savedDateRange.start,
          end: savedDateRange.end,
          customStringSelect: savedDateRange.customStringSelect
        });
      } else {
        const today = LuxonDateTime.local();
        this.endDate = today.toISODate();
        this.startDate = today.minus({ days: 7 }).toISODate();
        this.dateRangeForm.patchValue({ customStringSelect: 'last_week' });
      }

      // Restore selected workflow or use default
      const savedWorkflowId = await this.storage.retrieve(this.STORAGE_KEYS.SELECTED_WORKFLOW);
      this.selectedWorkflowValue = savedWorkflowId || 0;
      if (this.workflowItems.length > 0) {
        await this.selectWorkflow(this.selectedWorkflowValue);
      }

      this.loaded = true;
    } catch (err) {
      this.errorMessage = err.error ? err.error.message : err.message;
    }
  }

  async updateSearch({ search }) {
    if (!search || search.length < 2) {
      this.searchTerm = null;
      await this.storage.save(this.STORAGE_KEYS.SEARCH_TERM, null);
    } else {
      this.searchTerm = search;
      await this.storage.save(this.STORAGE_KEYS.SEARCH_TERM, search);
    }
    return this.getWorkflowsVersionRunHistory();
  }

  async buildWorkflowFilter() {
    this.workflowItems = [];
    this.workflows = await this.workflowsProvider.getWorkflows();
    this.workflows.map((workflow) => {
      this.workflowItems.push({
        viewValue: workflow.name,
        value: workflow.id,
      });
    });
    this.workflowItems.unshift({
      viewValue: 'All Workflows',
      value: 0,
    });
  }

  async selectCustomDateRange(selection) {
    this.workflowsLoaded = false;
    const start = new Date(selection.start);
    const end = new Date(selection.end);
    this.startDate = LuxonDateTime.fromJSDate(start).toISODate();
    this.endDate = LuxonDateTime.fromJSDate(end).toISODate();

    await this.storage.save(this.STORAGE_KEYS.DATE_RANGE, {
      start: this.startDate,
      end: this.endDate,
      customStringSelect: 'custom'
    });

    this.getWorkflowsVersionRunHistory();
  }

  async selectStringDateRange(selection) {
    this.workflowsLoaded = false;
    const today = LuxonDateTime.local();
    this.endDate = today.toISODate();
    if (selection === 'last_week') {
      this.startDate = today.minus({ days: 7 }).toISODate();
    } else if (selection === 'last_month') {
      this.startDate = today.minus({ months: 1 }).toISODate();
    } else if (selection === 'all_time') {
      this.startDate = '2023-8-10';
    } else if (selection === 'today') {
      this.startDate = today.toISODate();
    }

    await this.storage.save(this.STORAGE_KEYS.DATE_RANGE, {
      start: this.startDate,
      end: this.endDate,
      customStringSelect: selection
    });

    this.getWorkflowsVersionRunHistory();
  }

  async selectPage(page) {
    this.workflowsLoaded = false;
    this.pageNumber = page.pageIndex + 1;
    this.pageSize = page.pageSize;

    await this.storage.save(this.STORAGE_KEYS.PAGE_NUMBER, this.pageNumber);
    await this.storage.save(this.STORAGE_KEYS.PAGE_SIZE, this.pageSize);

    this.getWorkflowsVersionRunHistory();
  }

  async selectWorkflow(workflowIds) {
    try {
      this.workflowsLoaded = false;
      this.selectedWorkflowValue = workflowIds;
      const didSelectAllWorkflows = workflowIds === 0;
      if (didSelectAllWorkflows) {
        workflowIds = this.workflows.map((workflow) => workflow.id);
      } else {
        workflowIds = [workflowIds];
      }
      this.selectedWorkflowIds = workflowIds;

      await this.storage.save(this.STORAGE_KEYS.SELECTED_WORKFLOW, didSelectAllWorkflows ? 0 : workflowIds[0]);
      await this.getWorkflowsVersionRunHistory();
    } catch (err) {
      this.errorMessage = err.error ? err.error.message : err.message;
    }
  }

  async getWorkflowsVersionRunHistory() {
    const response = await this.workflowsProvider.getWorkflowVersionRuns(this.selectedWorkflowIds, this.startDate, this.endDate, this.pageNumber, this.pageSize, this.searchTerm);
    this.history = response.body;
    this.allHistoryLength = response.totalRecords || 100;
    this.workflowsLoaded = true;
  }

  goToWorkflowRunPage(run) {
    const params = {
      run,
      selectedWorkflow: '' //this.selectedWorkflow
    }
    this.goToPage('automations/workflow-history/workflow-run', params, WorkflowRunPage);
  }

  async goToPage(url, params, component) {
    if (this.isRightSideContent) {
      const isRightSidePanelAlreadyOpen = await this.storage.retrieve('dialog-right-side-open');
      if (isRightSidePanelAlreadyOpen) {
        return this.rightSidePanelService.navigateTo(url, params, component);
      } else {
        this.storage.save('dialog-right-side-open', true);
        this.storage.save('dialog-params', params);
        this.rightSidePanelService.openRightSidePanel(component);
      }
    } else {
      this.navCtrl.navigateForward(url, params);
    }
  }

  toggleSourceData(itemId: string) {
    this.expandedSourceData[itemId] = !this.expandedSourceData[itemId];
  }

  isSourceDataExpanded(itemId: string): boolean {
    return this.expandedSourceData[itemId] || false;
  }

}
